import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Link,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../utils/api';
import { useImmigrationWorkflowDefinition } from './useImmigrationWorkflowDefinition';

export const H1_LOTTERY_WF_LABELS = {
  n_a: 'N/A',
  step2_lottery_outcome: 'Step 2: Lottery result',
  lottery_not_picked: 'Not selected in lottery',
  step2b_in_project: 'Step 2b: Project placement',
  step_bench_marketing: 'Bench / marketing',
  step3_lca: 'Step 3: File LCA',
  step3b_public_notice: 'Publish (website & notice board)',
  step4_i129: 'Step 4: File I-129',
  step5_uscis_processing: 'Premium vs regular processing',
  step6_petition_decision: 'Petition approved or RFE',
  step7_rfe_response: 'RFE — attorney response',
  step8_courier: 'Courier / delivery routing',
  step9_candidate_received: 'Candidate received documents',
  workflow_complete: 'Complete',
  workflow_revoked: 'Revoked / closed',
};

const STEPS = ['Intake', 'LCA & notice', 'I-129', 'USCIS tracking', 'Delivery', 'Done'];

const DOL_FLAG_URL = 'https://flag.dol.gov/';

function stepIndex(wf) {
  if (wf === 'workflow_revoked') return 0;
  if (['step2_lottery_outcome', 'lottery_not_picked', 'step2b_in_project', 'step_bench_marketing'].includes(wf)) return 0;
  if (['step3_lca', 'step3b_public_notice'].includes(wf)) return 1;
  if (wf === 'step4_i129') return 2;
  if (['step5_uscis_processing', 'step6_petition_decision', 'step7_rfe_response'].includes(wf)) return 3;
  if (['step8_courier', 'step9_candidate_received'].includes(wf)) return 4;
  if (wf === 'workflow_complete') return 5;
  return 0;
}

function canUndoStep(wf) {
  return wf && wf !== 'step2_lottery_outcome' && wf !== 'workflow_revoked';
}

function extractImageFileFromClipboard(clipboardData) {
  if (!clipboardData) return null;
  const fromFiles = clipboardData.files?.[0];
  if (fromFiles?.type?.startsWith('image/')) {
    return fromFiles.name
      ? fromFiles
      : new File([fromFiles], 'screenshot.png', { type: fromFiles.type || 'image/png' });
  }
  const items = clipboardData.items;
  if (!items) return null;
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    if (it.kind === 'file' && it.type?.startsWith('image/')) {
      const f = it.getAsFile();
      if (f) return f.name ? f : new File([f], 'screenshot.png', { type: f.type || 'image/png' });
    }
  }
  return null;
}

async function downloadLotteryBlob(caseId, documentId, fileName) {
  const res = await api.get(`/immigration/h1-cases/${caseId}/lottery-attachments/${documentId}/download`, {
    responseType: 'blob',
  });
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function H1LotteryWorkflowDrawer({ caseId, open, onClose, onUpdated }) {
  const { data: h1WfDef } = useImmigrationWorkflowDefinition('h1_lottery');
  const wfLabels = { ...H1_LOTTERY_WF_LABELS, ...(h1WfDef?.stepLabels || {}) };

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const [benches, setBenches] = useState([]);
  const [benchId, setBenchId] = useState('');
  const [bcNotes, setBcNotes] = useState('');

  const [pubWebsite, setPubWebsite] = useState(false);
  const [pubNotice, setPubNotice] = useState(false);

  const [lcaDolCaseNumber, setLcaDolCaseNumber] = useState('');
  const [i129ReceiptNumber, setI129ReceiptNumber] = useState('');

  const [closeDialog, setCloseDialog] = useState(null);
  const [closeReason, setCloseReason] = useState('');

  const [courierReceived, setCourierReceived] = useState(false);
  const [courierDest, setCourierDest] = useState('');

  const regFileInputRef = useRef(null);
  const selFileInputRef = useRef(null);
  const uploadLotteryFileRef = useRef(async () => {});

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setErr('');
    try {
      const res = await api.get(`/immigration/h1-cases/${caseId}`);
      setRow(res.data?.data || null);
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to load case.');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (open && caseId) load();
  }, [open, caseId, load]);

  useEffect(() => {
    if (!row) return;
    setPubWebsite(Boolean(row.published_website ?? row.publishedWebsite));
    setPubNotice(Boolean(row.published_notice_board ?? row.publishedNoticeBoard));
    setLcaDolCaseNumber(String(row.lca_dol_case_number ?? row.lcaDolCaseNumber ?? ''));
    setI129ReceiptNumber(String(row.i129_receipt_number ?? row.i129ReceiptNumber ?? ''));
    setCourierReceived(Boolean(row.uscis_courier_received ?? row.uscisCourierReceived));
    setCourierDest(String(row.courier_sent_to ?? row.courierSentTo ?? ''));
  }, [row]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get('/bench-sales', { params: { limit: 200, page: 1 } });
        setBenches(res.data?.data?.benches || []);
      } catch {
        setBenches([]);
      }
    })();
  }, [open]);

  const patch = async (body) => {
    setBusy(true);
    setErr('');
    try {
      await api.patch(`/immigration/h1-cases/${caseId}/workflow`, body);
      await load();
      onUpdated?.();
    } catch (e) {
      setErr(e.response?.data?.message || 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  const uploadLotteryFile = useCallback(
    async (file, kind) => {
      setBusy(true);
      setErr('');
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', kind);
        await api.post(`/immigration/h1-cases/${caseId}/lottery-attachments`, fd);
        await load();
        onUpdated?.();
      } catch (e) {
        setErr(e.response?.data?.message || 'Upload failed.');
      } finally {
        setBusy(false);
      }
    },
    [caseId, load, onUpdated]
  );

  useEffect(() => {
    uploadLotteryFileRef.current = uploadLotteryFile;
  }, [uploadLotteryFile]);

  useEffect(() => {
    const step = row?.h1_workflow_step || row?.h1WorkflowStep;
    if (!open || !caseId || step !== 'step2_lottery_outcome') return undefined;
    const onPaste = (e) => {
      const ae = document.activeElement;
      if (ae) {
        const tag = ae.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || ae.isContentEditable) return;
      }
      const file = extractImageFileFromClipboard(e.clipboardData);
      if (!file) return;
      e.preventDefault();
      e.stopPropagation();
      uploadLotteryFileRef.current(file, 'registration');
    };
    window.addEventListener('paste', onPaste, true);
    return () => window.removeEventListener('paste', onPaste, true);
  }, [open, caseId, row?.h1_workflow_step, row?.h1WorkflowStep]);

  const deleteSelectionPdf = async () => {
    setBusy(true);
    setErr('');
    try {
      await api.delete(`/immigration/h1-cases/${caseId}/lottery-attachments/selection`);
      await load();
      onUpdated?.();
    } catch (e) {
      setErr(e.response?.data?.message || 'Remove failed.');
    } finally {
      setBusy(false);
    }
  };

  const deleteRegistrationScreenshotById = async (documentId) => {
    setBusy(true);
    setErr('');
    try {
      await api.delete(`/immigration/h1-cases/${caseId}/lottery-registration-screenshots/${documentId}`);
      await load();
      onUpdated?.();
    } catch (e) {
      setErr(e.response?.data?.message || 'Remove failed.');
    } finally {
      setBusy(false);
    }
  };

  const wf = row?.h1_workflow_step || row?.h1WorkflowStep;
  const contact = row?.contact;
  const closureKind = row?.h1_workflow_closure_kind || row?.h1WorkflowClosureKind;
  const petitionOutcome = row?.petition_outcome || row?.petitionOutcome;
  const pendingAfterRfe = petitionOutcome === 'pending_after_rfe';

  const openCloseDialog = (kind) => {
    setCloseReason('');
    setCloseDialog(kind);
  };

  const submitCloseDialog = async () => {
    const action = closeDialog === 'decline' ? 'candidate_decline' : 'revoke_fully';
    setCloseDialog(null);
    await patch({ action, reason: closeReason.trim() || undefined });
  };

  const lcaNum = row?.lca_dol_case_number || row?.lcaDolCaseNumber;
  const i129Num = row?.i129_receipt_number || row?.i129ReceiptNumber;
  const processingType = row?.uscis_processing_type || row?.uscisProcessingType;

  const regScreens = row?.lottery_registration_screenshots || row?.lotteryRegistrationScreenshots || [];
  const selDoc = row?.lotterySelectionDocument || row?.lottery_selection_document;
  const lotterySelected = row?.lottery_selected === true || row?.lotterySelected === true;
  const showSelectionPdf =
    lotterySelected &&
    wf &&
    !['lottery_not_picked', 'workflow_revoked', 'step2_lottery_outcome'].includes(wf);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !busy && onClose()}
      PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 2 } }}
    >
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        H1 lottery workflow
      </Typography>
      {contact && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {contact.first_name} {contact.last_name} · {contact.email}
        </Typography>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && row && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Current stage: <strong>{wfLabels[wf] || wf}</strong>
          </Alert>

          <Stepper activeStep={stepIndex(wf)} alternativeLabel sx={{ mb: 2 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>
              {err}
            </Alert>
          )}

          {wf === 'workflow_revoked' && (
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Alert severity="warning">
                {closureKind === 'candidate_declined'
                  ? 'The candidate declined to proceed (or similar closure).'
                  : 'This workflow was fully revoked or closed.'}
                {row.h1_workflow_revoke_reason || row.h1WorkflowRevokeReason ? (
                  <>
                    {' '}
                    <strong>Note:</strong> {row.h1_workflow_revoke_reason || row.h1WorkflowRevokeReason}
                  </>
                ) : null}
              </Alert>
              {row.h1_workflow_revoked_at || row.h1WorkflowRevokedAt ? (
                <Typography variant="caption" color="text.secondary">
                  Closed {new Date(row.h1_workflow_revoked_at || row.h1WorkflowRevokedAt).toLocaleString()}
                </Typography>
              ) : null}
              <Button variant="contained" color="success" disabled={busy} onClick={() => patch({ action: 'resume_workflow' })}>
                Resume workflow
              </Button>
              <Typography variant="caption" color="text.secondary">
                Resumes to the stage that was active when this case was closed.
              </Typography>
            </Stack>
          )}

          {wf !== 'workflow_revoked' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Workflow controls
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Button size="small" variant="outlined" disabled={busy || !canUndoStep(wf)} onClick={() => patch({ action: 'revoke_step' })}>
                  Undo last step
                </Button>
                <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => openCloseDialog('revoke')}>
                  Revoke fully
                </Button>
                <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => openCloseDialog('decline')}>
                  Candidate declined
                </Button>
              </Stack>
            </>
          )}

          {wf !== 'workflow_revoked' && <Divider sx={{ my: 2 }} />}

          {showSelectionPdf && (
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">USCIS selection notice (PDF)</Typography>
              <Typography variant="body2" color="text.secondary">
                After the candidate is selected, USCIS provides a PDF notice. Upload it here (replace anytime while the case is active).
              </Typography>
              <input
                type="file"
                accept="application/pdf,.pdf"
                hidden
                ref={selFileInputRef}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) uploadLotteryFile(f, 'selection');
                }}
              />
              {selDoc ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2">
                    <strong>{selDoc.file_name || selDoc.fileName || 'notice.pdf'}</strong>
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => downloadLotteryBlob(caseId, selDoc.id, selDoc.file_name || selDoc.fileName || 'selection-notice.pdf')}
                    disabled={busy}
                  >
                    Download
                  </Button>
                  <Button size="small" color="error" variant="outlined" disabled={busy} onClick={() => deleteSelectionPdf()}>
                    Remove
                  </Button>
                  <Button size="small" variant="outlined" disabled={busy} onClick={() => selFileInputRef.current?.click()}>
                    Replace PDF
                  </Button>
                </Stack>
              ) : (
                <Button variant="outlined" disabled={busy} onClick={() => selFileInputRef.current?.click()}>
                  Attach PDF from USCIS
                </Button>
              )}
            </Stack>
          )}

          {wf === 'step2_lottery_outcome' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Lottery registration confirmation (screenshots)</Typography>
              <Typography variant="body2" color="text.secondary">
                Use <strong>Shift+Command+Control+4</strong> (or your usual capture) on Mac, then press <strong>Command+V</strong> or{' '}
                <strong>Control+V</strong> while this drawer is open — the image on the clipboard is saved as PNG or JPEG. You can paste many
                screenshots. Use <strong>+</strong> to add another from a file, or remove individual thumbnails. (Focused text fields are not
                overwritten.)
              </Typography>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                hidden
                ref={regFileInputRef}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) uploadLotteryFile(f, 'registration');
                }}
              />
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  minHeight: 72,
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  With this drawer open, paste from the clipboard (Cmd+V or Ctrl+V) to add another screenshot — no need to click here first unless
                  you prefer focus inside the dashed area.
                </Typography>
              </Box>
              <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                {regScreens.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      maxWidth: 220,
                    }}
                  >
                    <ImageOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="caption" noWrap title={doc.file_name || doc.fileName}>
                      {doc.file_name || doc.fileName || 'image'}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Download"
                      onClick={() =>
                        downloadLotteryBlob(caseId, doc.id, doc.file_name || doc.fileName || 'screenshot.png')
                      }
                      disabled={busy}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Remove screenshot"
                      onClick={() => deleteRegistrationScreenshotById(doc.id)}
                      disabled={busy}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <IconButton
                  color="primary"
                  aria-label="Add screenshot from file"
                  onClick={() => regFileInputRef.current?.click()}
                  disabled={busy}
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    width: 48,
                    height: 48,
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Stack>
              <Button size="small" variant="text" disabled={busy} onClick={() => regFileInputRef.current?.click()}>
                Choose image file instead
              </Button>

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Step 2 — Was the candidate selected in the lottery?</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" color="success" disabled={busy} onClick={() => patch({ action: 'record_lottery_outcome', selected: true })}>
                  Yes, picked
                </Button>
                <Button variant="outlined" color="error" disabled={busy} onClick={() => patch({ action: 'record_lottery_outcome', selected: false })}>
                  No, not picked
                </Button>
              </Stack>
            </Stack>
          )}

          {wf === 'step2b_in_project' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Is the candidate currently on a client project?</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={busy} onClick={() => patch({ action: 'record_in_project', in_project: true })}>
                  Yes — on project
                </Button>
                <Button variant="outlined" disabled={busy} onClick={() => patch({ action: 'record_in_project', in_project: false })}>
                  No — not on project
                </Button>
              </Stack>
            </Stack>
          )}

          {wf === 'step_bench_marketing' && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Add the candidate to a bench for marketing. After saving, open the bench in Bench Sales to confirm the candidate appears on the
                Candidates tab (list counts were fixed to include all members).
              </Typography>
              {row.bench_candidate_id || row.benchCandidateId ? (
                <Alert severity="success">
                  Linked to bench:{' '}
                  <strong>
                    {row.marketingBench?.name || row.marketing_bench?.name || '—'}{' '}
                    {row.marketingBench?.bench_code || row.marketing_bench?.bench_code || ''}
                  </strong>
                  . View in <strong>Bench Sales</strong> for this bench to see the full candidate row.
                </Alert>
              ) : (
                <>
                  <FormControl fullWidth size="small">
                    <InputLabel>Bench</InputLabel>
                    <Select label="Bench" value={benchId} onChange={(e) => setBenchId(e.target.value)}>
                      {benches.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.name} ({b.bench_code || b.id.slice(0, 8)})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Notes for bench listing (optional)"
                    value={bcNotes}
                    onChange={(e) => setBcNotes(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <Button
                    variant="contained"
                    disabled={busy || !benchId}
                    onClick={() =>
                      patch({
                        action: 'add_to_bench',
                        bench_id: benchId,
                        notes: bcNotes.trim() || undefined,
                      })
                    }
                  >
                    Add to bench
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                disabled={busy || !(row.bench_candidate_id || row.benchCandidateId)}
                onClick={() => patch({ action: 'advance_to_lca_from_bench' })}
              >
                Continue to File LCA
              </Button>
            </Stack>
          )}

          {wf === 'step3_lca' && (
            <Stack spacing={2}>
              <Typography variant="body2">
                When the LCA is filed with DOL, enter the case number (shown on the certified LCA / case summary) so you can track status on{' '}
                <Link href={DOL_FLAG_URL} target="_blank" rel="noopener noreferrer">
                  FLAG (DOL)
                </Link>
                .
              </Typography>
              <TextField
                size="small"
                label="LCA DOL case number (required)"
                value={lcaDolCaseNumber}
                onChange={(e) => setLcaDolCaseNumber(e.target.value)}
                fullWidth
                required
              />
              <Button
                variant="contained"
                disabled={busy || !lcaDolCaseNumber.trim()}
                onClick={() => patch({ action: 'mark_lca_filed', lca_dol_case_number: lcaDolCaseNumber.trim() })}
              >
                Mark LCA filed
              </Button>
            </Stack>
          )}

          {wf === 'step3b_public_notice' && (
            <Stack spacing={2}>
              {lcaNum ? (
                <Typography variant="caption" color="text.secondary">
                  LCA case number on file: <strong>{lcaNum}</strong>
                </Typography>
              ) : null}
              <Typography variant="body2">
                After LCA filing, publish the required notice on the company website and on the physical notice board. Check each when done; saving
                with both checked moves the case to I-129 preparation.
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={pubWebsite} onChange={(e) => setPubWebsite(e.target.checked)} disabled={busy} />}
                label="Published on company website"
              />
              <FormControlLabel
                control={<Checkbox checked={pubNotice} onChange={(e) => setPubNotice(e.target.checked)} disabled={busy} />}
                label="Published on notice board"
              />
              <Button
                variant="contained"
                disabled={busy}
                onClick={() =>
                  patch({
                    action: 'update_public_notice',
                    website_published: pubWebsite,
                    notice_board_published: pubNotice,
                  })
                }
              >
                Save publication status
              </Button>
              {pubWebsite && pubNotice && <Alert severity="success">Both channels are marked — save to advance to Form I-129.</Alert>}
            </Stack>
          )}

          {wf === 'step4_i129' && (
            <Stack spacing={2}>
              <Typography variant="body2">After USCIS accepts the petition, enter the receipt number from the I-797C (Notice of Action), then mark filed.</Typography>
              <TextField
                size="small"
                label="I-129 / USCIS receipt number (required)"
                value={i129ReceiptNumber}
                onChange={(e) => setI129ReceiptNumber(e.target.value)}
                fullWidth
                required
              />
              <Button
                variant="contained"
                disabled={busy || !i129ReceiptNumber.trim()}
                onClick={() => patch({ action: 'mark_i129_filed', i129_receipt_number: i129ReceiptNumber.trim() })}
              >
                Mark I-129 filed
              </Button>
            </Stack>
          )}

          {wf === 'step5_uscis_processing' && (
            <Stack spacing={2}>
              {i129Num ? (
                <Typography variant="caption" color="text.secondary">
                  Receipt on file: <strong>{i129Num}</strong>
                </Typography>
              ) : null}
              <Typography variant="subtitle2">Premium processing or regular?</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={busy} onClick={() => patch({ action: 'set_uscis_processing_type', processing_type: 'premium' })}>
                  Premium processing
                </Button>
                <Button variant="outlined" disabled={busy} onClick={() => patch({ action: 'set_uscis_processing_type', processing_type: 'regular' })}>
                  Regular processing
                </Button>
              </Stack>
            </Stack>
          )}

          {wf === 'step6_petition_decision' && (
            <Stack spacing={2}>
              <Typography variant="caption" color="text.secondary">
                Processing: <strong>{processingType === 'premium' ? 'Premium' : processingType === 'regular' ? 'Regular' : '—'}</strong>
              </Typography>
              {pendingAfterRfe ? (
                <>
                  <Alert severity="info">RFE response was submitted. Record when USCIS approves the petition.</Alert>
                  <Button variant="contained" color="success" disabled={busy} onClick={() => patch({ action: 'set_petition_outcome', outcome: 'approved' })}>
                    Petition approved (after RFE)
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2">Was the petition approved, or did USCIS issue an RFE?</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="contained" color="success" disabled={busy} onClick={() => patch({ action: 'set_petition_outcome', outcome: 'approved' })}>
                      Approved
                    </Button>
                    <Button variant="outlined" color="warning" disabled={busy} onClick={() => patch({ action: 'set_petition_outcome', outcome: 'rfe' })}>
                      RFE received
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          )}

          {wf === 'step7_rfe_response' && (
            <Stack spacing={2}>
              <Typography variant="body2">
                Work with immigration counsel to prepare and submit the RFE response. When the response has been filed with USCIS, mark it here.
              </Typography>
              <Button variant="contained" disabled={busy} onClick={() => patch({ action: 'mark_rfe_response_submitted' })}>
                RFE response submitted to USCIS
              </Button>
            </Stack>
          )}

          {wf === 'step8_courier' && (
            <Stack spacing={2}>
              <Typography variant="body2">After approval, track physical delivery of the approval packet.</Typography>
              <FormControlLabel
                control={<Checkbox checked={courierReceived} onChange={(e) => setCourierReceived(e.target.checked)} disabled={busy} />}
                label="We received the courier / packet from USCIS (employer side)"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Sent to</InputLabel>
                <Select label="Sent to" value={courierDest} onChange={(e) => setCourierDest(e.target.value)} disabled={busy}>
                  <MenuItem value="">
                    <em>Select…</em>
                  </MenuItem>
                  <MenuItem value="candidate">Candidate (direct)</MenuItem>
                  <MenuItem value="petitioner_office">Employer / office (e.g. Pilvi)</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={busy || !courierDest}
                onClick={() =>
                  patch({
                    action: 'complete_courier_details',
                    uscis_courier_received: courierReceived,
                    courier_sent_to: courierDest,
                  })
                }
              >
                Save courier routing & continue
              </Button>
            </Stack>
          )}

          {wf === 'step9_candidate_received' && (
            <Stack spacing={2}>
              <Typography variant="body2">
                Final step: confirm the candidate has received their I-797 and related documents (when routed to them, or after internal handoff).
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Courier was marked for:{' '}
                <strong>
                  {(row.courier_sent_to || row.courierSentTo) === 'candidate'
                    ? 'Candidate'
                    : (row.courier_sent_to || row.courierSentTo) === 'petitioner_office'
                      ? 'Employer office'
                      : '—'}
                </strong>
              </Typography>
              <Button variant="contained" color="success" disabled={busy} onClick={() => patch({ action: 'mark_candidate_received_i797' })}>
                Candidate received documents — close workflow
              </Button>
            </Stack>
          )}

          {wf === 'lottery_not_picked' && <Alert severity="warning">This case is closed — candidate was not selected in the lottery.</Alert>}

          {wf === 'workflow_complete' && (
            <Alert severity="success">
              Workflow complete. LCA: {lcaNum || '—'} · I-129 receipt: {i129Num || '—'}
              {(row.candidate_received_i797_at || row.candidateReceivedI797At) && (
                <> · Documents received {new Date(row.candidate_received_i797_at || row.candidateReceivedI797At).toLocaleDateString()}</>
              )}
            </Alert>
          )}
        </>
      )}

      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </Box>

      <Dialog open={Boolean(closeDialog)} onClose={() => !busy && setCloseDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{closeDialog === 'decline' ? 'Candidate declined' : 'Revoke workflow fully'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {closeDialog === 'decline'
              ? 'Use this when the candidate was selected or in progress but chooses not to continue. You can resume later if they change their mind.'
              : 'Closes the workflow as revoked. The current stage is saved so you can resume later.'}
          </Typography>
          <TextField
            label="Reason / notes (optional)"
            fullWidth
            multiline
            minRows={2}
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialog(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color={closeDialog === 'decline' ? 'error' : 'warning'} disabled={busy} onClick={submitCloseDialog}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
